import { storageImage, storageSrcSet } from "@/lib/imageUrl";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, Camera, Building2, Package, Users, Sparkles, Plane, ImageIcon, ArrowRight, Share2, Phone, Mail } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { BookingDialog } from "@/components/BookingDialogLazy";
import { SEOHead } from "@/components/SEOHead";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Process from "@/components/Process";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useAdvancedScrollAnimation, useStaggeredAnimation } from "@/hooks/useAdvancedScrollAnimation";
import { ParallaxLayer } from "@/components/animations/ParallaxSection";
import { Card3D } from "@/components/animations/Card3D";
import { MagneticButton } from "@/components/animations/MagneticButton";
import { WordByWordReveal } from "@/components/animations/WordByWordReveal";
import { AnimatedGradientText } from "@/components/animations/AnimatedGradientText";
import { FloatingParticles } from "@/components/animations/FloatingParticles";
import { ScrollIndicator } from "@/components/animations/ScrollIndicator";
import { trackWhatsAppClick, trackPhoneClick, trackEmailClick } from "@/lib/analytics";
import { useProjects } from "@/hooks/useProjects";
import { LazyCoverImage } from "@/components/LazyCoverImage";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import Autoplay from "embla-carousel-autoplay";
import { supabaseProjectManager } from "@/lib/supabaseProjectManager";
import { MarketFilter } from "@/components/MarketFilter";
import { useHeroImage } from "@/hooks/useHeroImages";
import { RegionalFAQSection } from "@/components/seo/RegionalFAQSection";
import PricingTransparency from "@/components/PricingTransparency";
import { Badge } from "@/components/ui/badge";
import GoogleRating from "@/components/GoogleRating";
import { BreadcrumbSchema, ServiceSchema } from "@/components/StructuredData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PhotographyServicesGrid } from "@/components/PhotographyServicesGrid";

const Photography = () => {
  const [showBooking, setShowBooking] = useState(false);
  const heroAnim = useAdvancedScrollAnimation({ variant: 'fadeUp', duration: 1000 });
  const portfolioAnim = useAdvancedScrollAnimation({ variant: 'scale', threshold: 0.2 });
  const servicesAnim = useAdvancedScrollAnimation({ variant: 'fadeUp', threshold: 0.1 });
  const whyUsAnim = useAdvancedScrollAnimation({ variant: 'fadeLeft', threshold: 0.15 });
  
  const { data: projects = [], isLoading: projectsLoading } = useProjects({ publicOnly: true });
  const { data: heroImageData } = useHeroImage("photography");
  const [selectedMarket, setSelectedMarket] = useState<'de' | 'en'>('de');
  const photographyProjects = projects.filter(p => p.category === "photography" && p.language === selectedMarket);
  
  // Fallback to portfolio image if no hero image set
  const heroProject = photographyProjects.find(p => p.slug === "hanin-villa") || photographyProjects[0];
  const heroImageUrl = heroImageData?.image_url;

  const whatsappHref = `https://wa.me/4917670027200?text=${encodeURIComponent("Hallo Clicks DMP, ich interessiere mich für Fotografie Services!")}`;
  const phoneHref = "tel:+4917670027200";
  const emailHref = "mailto:info@clicksdmp.com?subject=Anfrage%20Fotografie%20Services";

  const handleWhatsApp = () => {
    trackWhatsAppClick('photography_page');
  };

  const handlePhone = () => {
    trackPhoneClick();
  };

  const handleEmail = () => {
    trackEmailClick();
  };

  const photographyServices = [
    {
      title: "Businessporträts & Corporate Headshots",
      subtitle: "Ihre professionelle Visitenkarte",
      description: "Erste Eindrücke zählen. Professionelle Businessporträts zeigen Kompetenz, Vertrauen und Persönlichkeit – genau das, was Ihre Zielgruppe sehen soll. Ob für Website, LinkedIn oder Geschäftsunterlagen: Wir inszenieren Sie so, dass Ihre Expertise sofort sichtbar wird.",
      icon: Users,
      keywords: ["business", "corporate", "portrait", "team"]
    },
    {
      title: "Immobilien- & Architekturfotografie",
      subtitle: "Räume, die verkaufen",
      description: "Eine Immobilie zeigt man nicht – man inszeniert sie. Wir fotografieren Ihre Objekte so, dass potenzielle Käufer:innen oder Mieter:innen sich sofort wohlfühlen und die Qualität auf den ersten Blick erkennen. Mit perfektem Licht, optimalen Perspektiven und cinematischer Bildsprache.",
      icon: Building2,
      keywords: ["immobilien", "real estate", "architektur", "interior"]
    },
    {
      title: "Produkt- & Foodfotografie",
      subtitle: "Produkte, die Appetit machen",
      description: "Ihr Produkt ist einzigartig – und genau so sollte es auch fotografiert werden. Ob E-Commerce, Social Media oder Printanzeigen: Wir setzen Ihre Produkte perfekt in Szene – mit allen Details, die überzeugen.",
      icon: Package,
      keywords: ["produkt", "food", "restaurant", "küche", "harvest"]
    },
    {
      title: "Lifestyle- & Teamporträts",
      subtitle: "Echte Menschen, echte Geschichten",
      description: "Ihre Unternehmenskultur verdient mehr als gestellte Stockfotos. Wir zeigen, wer Sie wirklich sind – mit authentischen Bildern Ihres Teams, die Nähe schaffen und Ihre Werte sichtbar machen.",
      icon: Users,
      keywords: ["lifestyle", "team", "people", "kultur"]
    },
    {
      title: "Kampagnenshootings",
      subtitle: "Visuelle Kampagnen mit Wirkung",
      description: "Ihre Markenbotschaft braucht starke Bilder. Wir entwickeln und realisieren visuelle Kampagnen, die im Gedächtnis bleiben – von der Idee über das Shooting bis zur finalen Bildbearbeitung.",
      icon: Sparkles,
      keywords: ["kampagne", "brand", "luxus", "hotel", "marken"]
    },
    {
      title: "Editorial- & Magazinfotografie",
      subtitle: "Geschichten, die man sieht",
      description: "Ob für Magazine, Blogs oder Unternehmensmagazine – wir erzählen Geschichten durch Bilder. Mit journalistischem Gespür, kreativer Bildsprache und höchster technischer Qualität.",
      icon: ImageIcon,
      keywords: ["editorial", "magazin", "story", "reportage"]
    },
    {
      title: "Social-Media-Fotografie",
      subtitle: "Content, der stoppt & konvertiert",
      description: "Social Media lebt von Aufmerksamkeit. Wir liefern Ihnen Content, der im Feed nicht untergeht – authentisch, kreativ und genau auf Ihre Zielgruppe abgestimmt.",
      icon: Sparkles,
      keywords: ["social", "media", "content", "instagram"]
    },
    {
      title: "Drohnenfotografie",
      subtitle: "Perspektiven, die beeindrucken",
      description: "Manchmal muss man die Dinge von oben sehen. Unsere Drohnenfotografie bietet beeindruckende Luftaufnahmen für Immobilien, Events, Unternehmen und Architekturprojekte.",
      icon: Plane,
      keywords: ["drohne", "drone", "aerial", "luftaufnahme"]
    },
    {
      title: "Model- & Fashionfotografie",
      subtitle: "Mode, die bewegt",
      description: "Fashion ist Emotion. Wir inszenieren Mode so, dass sie nicht nur gut aussieht, sondern eine Geschichte erzählt. Für Lookbooks, Kampagnen und Modelportfolios.",
      icon: Camera,
      keywords: ["fashion", "mode", "model", "style", "noir"]
    },
    {
      title: "Kampagnen- & Eventreportagen",
      subtitle: "Momente, die bewegen",
      description: "Events sind vergänglich – Bilder bleiben. Wir halten die Energie, Emotionen und besonderen Momente Ihrer Veranstaltung fest – diskret, professionell und mit Gespür für den richtigen Augenblick.",
      icon: Camera,
      keywords: ["event", "reportage", "dokumentation", "veranstaltung"]
    },
    {
      title: "Website-Fotografie",
      subtitle: "Bilder, die überzeugen",
      description: "Ihre Website ist Ihr digitales Schaufenster. Wir liefern hochwertige, professionelle Bilder, die Vertrauen schaffen, Ihre Marke stärken und Besucher:innen zu Kund:innen machen.",
      icon: ImageIcon,
      keywords: ["website", "web", "digital", "online"]
    }
  ];

  const whyChooseUs = [
    {
      title: "Authentische Bildsprache",
      description: "Wir fotografieren nicht einfach – wir erzählen Ihre Geschichte durch Bilder, die echt sind und berühren."
    },
    {
      title: "Höchste Qualität",
      description: "Professionelles Equipment, jahrelange Erfahrung und ein Auge fürs Detail – bei uns bekommen Sie Bilder, die herausstechen."
    },
    {
      title: "Individuelle Beratung",
      description: "Jedes Projekt ist einzigartig. Wir nehmen uns Zeit, Ihre Vision zu verstehen und gemeinsam mit Ihnen umzusetzen."
    },
    {
      title: "Schnelle Lieferung",
      description: "Sie brauchen Ihre Bilder zeitnah? Wir liefern schnell, ohne Kompromisse bei der Qualität."
    }
  ];

  return (
    <>
      <SEOHead
        title="Professionelle Fotografie | Business, Produkt & Lifestyle | Clicks DMP"
        description="Hochwertige Businessfotografie, Produktfotografie, Immobilien & mehr. Authentische Bilder, die Ihre Marke stärken und Vertrauen schaffen."
        canonical="https://clicksdmp.com/foto"
        ogImage="https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/covers/1760105191013-648ywxg9str.jpg"
      />
      
      <BreadcrumbSchema items={[{ name: "Startseite", url: "https://clicksdmp.com/" }, { name: "Fotografie", url: "https://clicksdmp.com/foto" }]} />
      <ServiceSchema serviceType="Fotografie" name="Fotografie in Mannheim und der Rhein-Neckar-Region" path="/foto" description="Hochwertige Businessfotografie, Produktfotografie, Immobilien- und Eventfotografie für Unternehmen." />

      <div className="min-h-screen bg-background overflow-x-hidden">
        <Header />
        
        {/* Hero Section with Apple-Style Parallax */}
        <section 
          id="hero" 
          className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background"
          style={{
            animation: 'page-entry 1.5s ease-out forwards',
            opacity: 0
          }}
        >
          {/* Layer 1: Background Image (slowest parallax) */}
          <ParallaxLayer speed={0.3} className="absolute inset-0">
            {heroImageUrl ? (
              <img
                src={storageImage(heroImageUrl, 1600, { quality: 78 })}
                srcSet={storageSrcSet(heroImageUrl, [800, 1200, 1600], { quality: 78 })}
                sizes="100vw"
                fetchPriority="high"
                alt="Photography Hero"
                className="w-full h-full object-cover"
                loading="eager"
              />
            ) : heroProject && (
              <LazyCoverImage
                projectId={heroProject.id}
                category={heroProject.category}
                title={heroProject.title}
                className="w-full h-full object-cover"
              />
            )}
          </ParallaxLayer>

          {/* Layer 2: Gradient Overlay */}
          <ParallaxLayer speed={0.5} className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-background" />
          </ParallaxLayer>

          {/* Layer 3: Floating Particles */}
          <ParallaxLayer speed={0.7} className="absolute inset-0">
            <FloatingParticles count={6} />
          </ParallaxLayer>

          {/* Layer 4: Text Content */}
          <ParallaxLayer speed={0.8} className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div className="container-custom text-center max-w-5xl">
              <div className="flex flex-col items-center gap-6">
                {/* Animated Headline */}
                <WordByWordReveal
                  text="Professionelle Fotografie in Mannheim und Rhein-Neckar"
                  className="text-4xl md:text-6xl lg:text-7xl font-bold text-white"
                  startDelay={1000}
                  wordDelay={100}
                />
                <WordByWordReveal
                  as="p"
                  text="Professionelle Fotografie"
                  className="text-4xl md:text-6xl lg:text-7xl font-bold text-white"
                  startDelay={1400}
                  wordDelay={100}
                />
                
                {/* Animated Gradient Subtitle */}
                <AnimatedGradientText
                  className="text-2xl md:text-4xl lg:text-5xl font-bold"
                  delay={1800}
                >
                  Mannheim & Rhein-Neckar
                </AnimatedGradientText>
                
                {/* Description */}
                <p 
                  className="text-base md:text-lg text-gray-300 max-w-2xl"
                  style={{
                    animation: 'fade-in 0.8s ease-out forwards',
                    animationDelay: '2.3s',
                    opacity: 0
                  }}
                >
                  Hochwertige Fotografie für Business, Events und Portraits – 
                  Ihr Partner in Mannheim, Heidelberg und der gesamten Rhein-Neckar-Region
                </p>

                {/* CTA Buttons */}
                <div 
                  className="flex flex-col sm:flex-row gap-4 mt-4 pointer-events-auto"
                  style={{
                    animation: 'fade-in 0.8s ease-out forwards',
                    animationDelay: '2.5s',
                    opacity: 0
                  }}
                >
                  <MagneticButton
                    onClick={() => setShowBooking(true)}
                    className="border-primary/30 bg-primary/5 text-primary backdrop-blur-xs hover:bg-primary/10 px-8 py-4 text-lg rounded-full font-semibold transition-all border flex items-center justify-center gap-2"
                    strength={0.3}
                  >
                    <Calendar className="w-5 h-5" />
                    Jetzt Beratung buchen
                  </MagneticButton>
                  <MagneticButton
                    onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                    className="border-primary/30 bg-primary/5 text-primary backdrop-blur-xs hover:bg-primary/10 px-8 py-4 text-lg rounded-full font-semibold transition-all border flex items-center justify-center gap-2"
                    strength={0.3}
                  >
                    <Camera className="w-5 h-5" />
                    Portfolio ansehen
                  </MagneticButton>
                </div>
              </div>
            </div>
          </ParallaxLayer>

          {/* Scroll Indicator */}
          <ScrollIndicator targetId="regional-services" delay={2500} />
        </section>

        {/* Regional Service Areas Section */}
        <section id="regional-services" className="section-padding bg-gradient-to-b from-background to-primary/5">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Fotografie in der <span className="text-gradient">Rhein-Neckar-Region</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Mit Sitz in Mannheim sind wir Ihre professionellen Fotografen für die gesamte Region – von Heidelberg über Ludwigshafen und Karlsruhe bis Stuttgart, Frankfurt, Mainz und Wiesbaden. Wir bringen unsere Expertise in Business-Fotografie, Produktfotografie und Immobilienfotografie direkt zu Ihnen in den Südwesten Deutschlands.
              </p>
            </div>

            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Was macht <span className="text-gradient">großartige Fotografie</span> aus?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Es ist mehr als nur auf den Auslöser zu drücken
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { num: "01", title: "Das richtige Licht", desc: "Licht macht den Unterschied zwischen einem Smartphone-Schnappschuss und einem Bild, das Emotionen weckt. Wir beherrschen natürliches Licht, Studio-Setups und alles dazwischen." },
                { num: "02", title: "Die perfekte Komposition", desc: "Jedes Element im Bild hat einen Grund. Von der Perspektive über den Bildaufbau bis zur Farbharmonie – wir komponieren Bilder, die ins Auge fallen und im Kopf bleiben." },
                { num: "03", title: "Authentische Momente", desc: "Die besten Bilder entstehen, wenn Menschen sich wohlfühlen. Wir schaffen eine Atmosphäre, in der Authentizität entsteht – keine gestellten Posen, sondern echte Persönlichkeit." }
              ].map((item, index) => {
                const anim = useStaggeredAnimation(3, 200, 150)[index];
                if (!anim) return null;
                return (
                  <div key={index} ref={anim.ref} style={anim.style}>
                    <Card3D className="relative group h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative bg-background border border-primary/20 rounded-3xl p-8 h-full hover:border-primary/40 transition-all duration-300">
                        <div className="text-6xl font-bold text-primary/20 mb-4">{item.num}</div>
                        <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                      </div>
                    </Card3D>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Trust & Results Section */}
        <section id="trust" className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Vertrauen beginnt mit dem <span className="text-gradient">ersten Blick</span>
                </h2>
                <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                  In weniger als 3 Sekunden entscheiden Ihre Website-Besucher, ob sie bleiben oder gehen. 
                  Professionelle Fotografie ist der schnellste Weg, Vertrauen aufzubauen, Kompetenz zu zeigen 
                  und den entscheidenden ersten Eindruck zu machen.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-6 bg-primary/5 rounded-2xl border border-primary/10">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Für Online-Shops & E-Commerce</h3>
                      <p className="text-muted-foreground">
                        Produktfotos, die nicht nur zeigen, sondern verkaufen. Mit perfektem Licht, 
                        allen Details und in verschiedenen Perspektiven.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-6 bg-primary/5 rounded-2xl border border-primary/10">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Für Immobilien & Architektur</h3>
                      <p className="text-muted-foreground">
                        Räume, die Emotionen wecken. Mit HDR-Technik, optimalen Perspektiven und 
                        Drohnenaufnahmen für beeindruckende Außenansichten.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-6 bg-primary/5 rounded-2xl border border-primary/10">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Für Unternehmen & Personal Brands</h3>
                      <p className="text-muted-foreground">
                        Porträts mit Persönlichkeit. Wir zeigen nicht nur Gesichter, sondern die Menschen 
                        dahinter – authentisch, professionell und sympathisch.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-primary/10 rounded-3xl blur-3xl" />
                <div className="relative space-y-6">
                  <div className="bg-gradient-to-br from-background to-primary/5 border border-primary/20 rounded-3xl p-8">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-2xl font-bold">Zahlen, die überzeugen</h3>
                      <Camera className="w-8 h-8 text-primary" />
                    </div>
                    
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className="text-5xl font-bold text-primary">67%</div>
                        <p className="text-sm text-muted-foreground">
                          der Online-Käufer sagen, dass die Bildqualität "sehr wichtig" für ihre Kaufentscheidung ist
                        </p>
                      </div>
                      
                      <div className="h-px bg-primary/20" />
                      
                      <div className="flex items-center gap-4">
                        <div className="text-5xl font-bold text-primary">93%</div>
                        <p className="text-sm text-muted-foreground">
                          aller Kommunikation ist visuell – hochwertige Bilder sind Ihr stärkster Kommunikationskanal
                        </p>
                      </div>
                      
                      <div className="h-px bg-primary/20" />
                      
                      <div className="flex items-center gap-4">
                        <div className="text-5xl font-bold text-primary">40%</div>
                        <p className="text-sm text-muted-foreground">
                          mehr Engagement erhalten Social-Media-Posts mit professionellen Bildern
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="section-padding bg-gradient-to-b from-background to-primary/5">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Unsere <span className="text-gradient">Leistungen</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Von Business-Porträts bis Event-Reportagen – wir decken alle Ihre fotografischen Anforderungen ab
              </p>
            </div>

            <PhotographyServicesGrid />

            <div className="flex flex-wrap gap-4 justify-center mt-12">
              <Button 
                size="lg" 
                className="group"
                onClick={() => setShowBooking(true)}
              >
                Jetzt anfragen
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                asChild
                variant="outline" 
                size="lg"
              >
                <a href={phoneHref} onClick={handlePhone}>
                  <Phone className="w-4 h-4 mr-2" />
                  Anrufen
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section ref={whyUsAnim.ref} className={`section-padding transition-all duration-1000 ${whyUsAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Warum <span className="text-gradient glow-text">Clicks DMP</span>?
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Mehr als nur Bilder – eine Partnerschaft für Ihren visuellen Erfolg
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="text-center p-8 rounded-2xl bg-gradient-to-b from-primary/5 to-transparent border border-primary/10 hover:border-primary/30 transition-all duration-300"
                >
                  <h3 className="text-xl font-bold mb-4">{reason.title}</h3>
                  <p className="text-muted-foreground">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Transparency Section */}
        <PricingTransparency serviceType="photography" />

        {/* Portfolio Section */}
        <section id="portfolio" ref={portfolioAnim.ref} className={`section-padding bg-gradient-to-b from-background to-primary/5 transition-all duration-1000 ${portfolioAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Unsere <span className="text-gradient glow-text">Fotografie</span> Projekte
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Sehen Sie, wie wir Marken durch professionelle Fotografie zum Leben erwecken
              </p>
              
              <MarketFilter 
                selectedMarket={selectedMarket}
                onMarketChange={setSelectedMarket}
                projectCount={photographyProjects.length}
              />
            </div>

            {projectsLoading ? (
              <div className="w-full max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl overflow-hidden">
                      <Skeleton className="aspect-[4/3] w-full" />
                      <div className="p-6 space-y-2">
                        <Skeleton className="h-6 w-3/4" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : photographyProjects.length > 0 ? (
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                  dragFree: false,
                  watchDrag: true,
                }}
                plugins={[
                  Autoplay({
                    delay: 2000,
                    stopOnInteraction: false,
                    stopOnMouseEnter: true,
                  })
                ]}
                className="w-full max-w-6xl mx-auto"
              >
                <CarouselContent className="-ml-4">
                  {photographyProjects.map((project) => (
                    <CarouselItem key={project.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                      <Link 
                        to={`/portfolio/${project.slug}`} 
                        className="group block"
                      >
                        <div className="relative overflow-hidden rounded-xl bg-card border border-border transition-all duration-500 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/20 hover:-translate-y-2">
                          {/* Image */}
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <LazyCoverImage
                              projectId={project.id}
                              category={project.category}
                              title={project.title}
                              className="w-full h-full"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                            
                            {/* Category Badge */}
                            <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-xs border border-border">
                              <Camera className="w-4 h-4 text-primary" />
                              <span className="text-xs capitalize">Fotografie</span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-6">
                            <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-1">
                              {project.title}
                            </h3>
                            <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                              {project.description}
                            </p>
                            
                            {project.client && (
                              <p className="text-xs text-muted-foreground mb-4">
                                Kunde: {project.client}
                              </p>
                            )}
                            
                            <div className="flex items-center gap-2 text-primary text-sm font-medium">
                              <span>Projekt ansehen</span>
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="hidden md:flex" />
                <CarouselNext className="hidden md:flex" />
              </Carousel>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  Wir arbeiten noch an unserer Website. Das Fotografie-Portfolio wird in Kürze verfügbar sein.
                </p>
              </div>
            )}

            {/* View All Portfolio Link */}
            {photographyProjects.length > 0 && (
              <div className="text-center mt-12">
                <Link to={`/portfolio?market=${selectedMarket}`}>
                  <Button variant="hero-outline" size="lg" className="group">
                    Alle Projekte ansehen
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Process Section */}
        <Process />

        <GoogleRating />

        {/* FAQ Section */}
        <RegionalFAQSection 
          title="Häufig gestellte Fragen zur Fotografie"
          subtitle="Alles, was Sie über professionelle Fotografie in Mannheim, Heidelberg, Karlsruhe und der Region wissen müssen"
          faqs={[
            {
              question: "In welchen Städten bieten Sie Fotografie-Services an?",
              answer: "Wir sind in der gesamten Rhein-Neckar-Region aktiv. Unser Hauptsitz ist in Mannheim, aber wir bedienen auch Heidelberg, Ludwigshafen, Karlsruhe, Stuttgart, Frankfurt, Mainz, Wiesbaden und alle umliegenden Städte. Für deutschlandweite Projekte sind wir ebenfalls verfügbar."
            },
            {
              question: "Was kostet ein professionelles Fotoshooting in Mannheim?",
              answer: "Die Kosten hängen vom Umfang des Projekts ab. Ein Business-Porträt-Shooting beginnt bei ca. 500€, während umfangreiche Produkt- oder Immobilienfotografie-Projekte individuell kalkuliert werden. Kontaktieren Sie uns für ein kostenloses Beratungsgespräch und ein maßgeschneidertes Angebot."
            },
            {
              question: "Wie lange dauert es, bis ich meine Bilder erhalte?",
              answer: "Die Standardbearbeitungszeit beträgt 7-14 Tage nach dem Shooting. Für eilige Projekte bieten wir Express-Service mit Lieferung innerhalb von 2-3 Werktagen. Alle Bilder werden professionell retuschiert und in hoher Auflösung geliefert."
            },
            {
              question: "Welche Branchen fotografieren Sie hauptsächlich?",
              answer: "Wir sind spezialisiert auf Business-Fotografie, Immobilien, Hotels & Gastronomie, Retail & E-Commerce, Events und Fashion. In der Rhein-Neckar-Region haben wir bereits mit zahlreichen Unternehmen aus verschiedensten Branchen zusammengearbeitet."
            },
            {
              question: "Benötige ich ein eigenes Studio für das Shooting?",
              answer: "Nein, wir bringen unser mobiles Studio zu Ihnen oder nutzen Ihre Räumlichkeiten. Bei Bedarf haben wir auch Zugang zu professionellen Studios in Mannheim und Heidelberg. Außerdem bieten wir On-Location-Shootings in der gesamten Region an."
            },
            {
              question: "Bieten Sie auch Retusche und Bildbearbeitung an?",
              answer: "Ja, professionelle Retusche ist bei allen unseren Paketen inklusive. Wir optimieren Farben, Kontraste, entfernen störende Elemente und sorgen dafür, dass jedes Bild perfekt aussieht – natürlich und authentisch, ohne übertrieben zu wirken."
            }
          ]}
        />

        {/* Final CTA */}
        <section className="section-padding bg-gradient-to-b from-background to-primary/10">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Bereit für <span className="text-gradient glow-text">professionelle Bilder</span>?
              </h2>
              <p className="text-xl text-muted-foreground mb-12">
                Lassen Sie uns gemeinsam Ihre visuelle Geschichte erzählen. Buchen Sie jetzt ein kostenloses Beratungsgespräch und erfahren Sie, wie wir Ihre Marke durch starke Bilder zum Leben erwecken.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button variant="hero" size="xl" onClick={() => setShowBooking(true)}>
                  <Calendar className="w-5 h-5" />
                  Jetzt Termin buchen
                </Button>
                <Button asChild variant="hero-outline" size="xl">
                  <a href={whatsappHref} target="_blank" rel="noopener" onClick={handleWhatsApp}>
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp Kontakt
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <BookingDialog open={showBooking} onOpenChange={setShowBooking} />
      </div>
    </>
  );
};

export default Photography;
